import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  flattenElements,
  getChildElements,
  getElementById,
  getAllBuildingVersions,
  isElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  ElementKind,
  type IElement,
  type OneOfElements,
  type Project,
} from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getChangeAffectedElementId } from './action.helpers';
import { remapProjectChangeElementIds } from './clean-actions.helpers';
import { stripRecipeResolvedElement } from './recipe-resolved-element-cache.helpers';

const emptyVersionMap = new Map<string, string>();

/**
 * Pairs source vs duplicate element ids in depth-first child order (same contract as
 * {@link addElements} duplication). Handles category-driven structural drift where
 * factory flatten length no longer matches the enriched duplicate.
 */
const buildDuplicateSubtreeElementIdMap = (
  source: IElement,
  target: IElement,
): Map<string, string> | undefined => {
  const map = new Map<string, string>();

  const walk = (a: OneOfElements, b: OneOfElements): boolean => {
    if (a.kind !== b.kind) return false;
    if (a.id === b.id) return false;
    map.set(a.id, b.id);

    if (isProductElement(a)) {
      if (!isProductElement(b)) return false;
      const ca = getChildElements(a);
      const cb = getChildElements(b);
      if (ca.length !== cb.length) return false;
      for (let i = 0; i < ca.length; i++) {
        const ac = ca[i];
        const bc = cb[i];
        if (!ac || !bc) return false;
        if (!walk(ac, bc)) return false;
      }
      return true;
    }
    if (!isElement(a) || !isElement(b)) return false;

    const ca = getChildElements(a);
    const cb = getChildElements(b);
    if (ca.length !== cb.length) return false;
    for (let i = 0; i < ca.length; i++) {
      const ac = ca[i];
      const bc = cb[i];
      if (!ac || !bc) return false;
      if (!walk(ac, bc)) return false;
    }
    return true;
  };

  return walk(source, target) && map.size > 0 ? map : undefined;
};

/**
 * Visibility edits on the source subtree must not be cloned — the duplicate flow already
 * records an {@link ProjectChangeType.ElementActivate} for the new root.
 */
const isSubtreeVisibilityChange = (change: ProjectChange): boolean =>
  change.type === ProjectChangeType.ElementActivate ||
  change.type === ProjectChangeType.ElementDeactivate ||
  change.type === ProjectChangeType.ElementSoftDelete;

/**
 * Collects remapped copies of existing proposal {@link ProjectChange} rows that target the
 * duplicated source subtree so the new element ids inherit the same proposal overrides
 * (quantity, properties, …) and stay editable under the proposal (DEV-3265).
 *
 * @param project - Project after baseline changes (including the duplicate add) were applied
 * @param proposalId - Selected proposal id
 * @param baselineChanges - Baseline partition from {@link partitionProjectChangesForProposalContext}
 */
export const collectRemappedProposalChangesForDuplicateSubtree = (
  project: Project,
  proposalId: ProposalId,
  baselineChanges: readonly ProjectChange[],
): ProjectChange[] => {
  const version = getAllBuildingVersions(project).find((candidate) =>
    candidate.proposals?.some((proposal) => proposal.id === proposalId),
  );
  if (!version?.actions?.length) return [];

  const proposal = version.proposals?.find((p) => p.id === proposalId);
  if (!proposal) return [];

  const out: ProjectChange[] = [];

  for (const change of baselineChanges) {
    if (change.type !== ProjectChangeType.ElementAdd) continue;

    const sourceRootId = change.duplicateSubtreeSourceElementId;
    if (!sourceRootId) continue;
    if (change.factory.kind !== ElementKind.Element) continue;

    const targetRootId = change.factory.id;
    if (!guards.is.string(targetRootId)) continue;

    const sourceEl = getElementById(project, sourceRootId, false);
    const targetEl = getElementById(project, targetRootId, false);
    if (!sourceEl || !targetEl) continue;
    if (!isElement(sourceEl) || !isElement(targetEl)) continue;

    // When subtree shape diverges (category enrichment, child-count drift), only the
    // root id pair is remapped — descendant proposal overrides keep source element ids.
    const elementMap =
      buildDuplicateSubtreeElementIdMap(sourceEl, targetEl) ??
      new Map<string, string>([[sourceRootId, targetRootId]]);
    if (elementMap.size === 0) continue;

    const subtreeIds = new Set(
      flattenElements(sourceEl).map((node) => node.id),
    );

    for (const actionId of proposal.actionIds ?? []) {
      const action = version.actions.find((a) => a.id === actionId);
      if (!action) continue;
      for (const ch of action.changes) {
        const affected = getChangeAffectedElementId(ch);
        if (!affected || !subtreeIds.has(affected)) continue;
        if (isSubtreeVisibilityChange(ch)) continue;
        out.push(remapProjectChangeElementIds(ch, elementMap, emptyVersionMap));
      }
    }
  }

  return stripRecipeResolvedElement(out);
};
